/**
 * clearClientSession
 *
 * Synchronously clears all client-side session state without making any API
 * calls. Called both on explicit logout (useLogout) and on 401-triggered
 * unauthorized (handleUnauthorized in router.tsx) so the two paths are
 * always in sync.
 *
 * Import graph is intentionally kept cycle-free:
 *   clear-client-session.ts  →  query-client.ts  (leaf)
 *                             →  stores/app.ts    (leaf)
 *                             →  stores/redirect.ts (leaf)
 *                             →  config/queryKeys.ts (leaf)
 */
import { authQueryKeys } from '@/config/queryKeys';
import { useAppStore } from '@/stores/app';
import { useRedirectStore } from '@/stores/redirect';

import { queryClient } from './query-client';

export function clearClientSession(): void {
  // Mark auth cache as signed out immediately so any observers see null
  try {
    queryClient.setQueryData(authQueryKeys.me(), null);
  } catch {}

  // Clear the full TanStack Query cache
  try {
    queryClient.clear();
  } catch {}

  // Collapse sidebar and any open org flyout (UI state only — no data loss). Otherwise the flag
  // survives the redirect and the next selected organization can mount with the flyout already open.
  try {
    useAppStore.getState().setIsSidebarMenuOpen(false);
  } catch {}
  try {
    useAppStore.getState().setIsOrgSelectorOpen(false);
  } catch {}

  // Clear runtime user and organization state so the app doesn't render with
  // stale data after session expiry. The persisted selectedOrgId/selectedOrgStub
  // are intentionally retained so the user returns to the same org after re-login.
  try {
    useAppStore.getState().setUser(undefined);
  } catch {}
  try {
    useAppStore.getState().setSelectedOrganization(undefined);
  } catch {}

  // Clear any pending post-login redirect path
  try {
    useRedirectStore.getState().setRedirectPath(null);
  } catch {}
  // The store's own API rather than its persist key by name — a rename of that key would otherwise
  // silently resurrect the stale redirect, with every test still green.
  try {
    useRedirectStore.persist.clearStorage();
  } catch {}

  // Remove nav lock so the login page doesn't get suppressed
  try {
    sessionStorage.removeItem('__nav_lock_until');
  } catch {}

  // Record logout timestamp for quick re-login detection in polling/guards
  try {
    sessionStorage.setItem('__last_logout_ts', String(Date.now()));
  } catch {}
}
